import {
  BufferAttribute,
  BufferGeometry,
  Camera,
  ColorSpace,
  Material,
  Mesh,
  MeshBasicMaterial,
  SRGBColorSpace,
  Texture,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { TileCoord, WorldMapping, WorldQuad } from './ortho-tile-typings';

function buildQuadGeometry(q: WorldQuad): BufferGeometry {
  // Two triangles: (a,b,d) and (b,c,d)
  // UVs mapped to (0,0) (1,0) (1,1) (0,1)
  const geom = new BufferGeometry();

  const positions = new Float32Array([
    // tri 1
    q.a.x,
    q.a.y,
    q.a.z,
    q.b.x,
    q.b.y,
    q.b.z,
    q.d.x,
    q.d.y,
    q.d.z,
    // tri 2
    q.b.x,
    q.b.y,
    q.b.z,
    q.c.x,
    q.c.y,
    q.c.z,
    q.d.x,
    q.d.y,
    q.d.z,
  ]);

  const uvs = new Float32Array([
    // tri 1
    0, 0, 1, 0, 0, 1,
    // tri 2
    1, 0, 1, 1, 0, 1,
  ]);

  geom.setAttribute('position', new BufferAttribute(positions, 3));
  geom.setAttribute('uv', new BufferAttribute(uvs, 2));
  geom.computeVertexNormals(); // harmless for BasicMaterial; useful if you later swap materials

  return geom;
}

type FlatPlaneOptions = {
  /**
   * User-provided conversion from tile coord -> world quad.
   * This keeps FlatPlaneWorldMapping generic and reusable.
   */
  tileBoundsToWorldQuad: (coord: TileCoord) => WorldQuad;

  // Texture/material settings
  anisotropy?: number;
  colorSpace?: ColorSpace;
  generateMipmaps?: boolean;

  transparent?: boolean;
  opacity?: number;
  depthWrite?: boolean;
  polygonOffset?: { factor: number; units: number };
  renderOrder?: number;

  /**
   * Optional function to generate a material for a tile when texture is applied.
   * If provided, this will be used instead of the default MeshBasicMaterial.
   * The function receives the texture and should return a Material instance.
   */
  materialGenerator?: (texture: Texture) => Material;
};

/**
 * A default flat-plane mapping example (EPSG:3857-ish in spirit),
 * BUT intentionally abstract about converting (z/x/y -> world units).
 *
 * You would plug in your own tile->world conversion in `tileBoundsToWorldQuad`.
 */
export class FlatPlaneWorldMapping implements WorldMapping {
  constructor(private readonly opts: FlatPlaneOptions) {}

  tileToWorldGeometry(input: { coord: TileCoord }): BufferGeometry {
    const quad = this.tileBoundsToWorldQuad(input.coord);
    return buildQuadGeometry(quad);
  }

  getTileWorldCorners(coord: TileCoord): Vector3[] {
    const quad = this.tileBoundsToWorldQuad(coord);
    return [quad.a, quad.b, quad.c, quad.d];
  }

  estimateScreenPx(cornersWorld: Vector3[], camera: Camera, renderer: WebGLRenderer): number {
    const size = renderer.getSize(new Vector2());
    const w = size.x;
    const h = size.y;

    const pts = cornersWorld.map((p) => p.clone().project(camera));

    // If all points are behind camera / beyond far clip, ignore
    if (pts.every((p) => p.z > 1)) {
      return 0;
    }

    // If all points are outside on same side in NDC, ignore
    if (pts.every((p) => p.x < -1)) {
      return 0;
    }
    if (pts.every((p) => p.x > 1)) {
      return 0;
    }
    if (pts.every((p) => p.y < -1)) {
      return 0;
    }
    if (pts.every((p) => p.y > 1)) {
      return 0;
    }

    // Now compute bbox *without clamping*
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (const p of pts) {
      const x = (p.x + 1) * 0.5 * w;
      const y = (1 - (p.y + 1) * 0.5) * h;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    return Math.max(maxX - minX, maxY - minY);
  }

  createTileMesh(geometry: BufferGeometry): Mesh {
    const mat = new MeshBasicMaterial({
      transparent: this.opts.transparent ?? false,
      opacity: this.opts.opacity ?? 1,
      depthWrite: this.opts.depthWrite ?? true,
    });

    if (this.opts.polygonOffset) {
      mat.polygonOffset = true;
      mat.polygonOffsetFactor = this.opts.polygonOffset.factor;
      mat.polygonOffsetUnits = this.opts.polygonOffset.units;
    }

    const mesh = new Mesh(geometry, mat);
    mesh.frustumCulled = true;
    mesh.renderOrder = this.opts.renderOrder ?? 0;

    return mesh;
  }

  applyTexture(mesh: Mesh, texture: Texture): void {
    // If a material generator is provided, use it to create a new material
    if (this.opts.materialGenerator) {
      const newMaterial = this.opts.materialGenerator(texture);
      const oldMaterial = mesh.material;
      mesh.material = newMaterial;

      // Dispose old material if it exists
      if (oldMaterial instanceof Material) {
        oldMaterial.dispose();
      } else if (Array.isArray(oldMaterial)) {
        oldMaterial.forEach((m) => m.dispose());
      }
    } else {
      // Default behavior: apply texture to existing material
      const mat = mesh.material as MeshBasicMaterial;
      mat.map = texture;
      mat.map.colorSpace = this.opts.colorSpace ?? SRGBColorSpace;
      mat.needsUpdate = true;
    }
  }

  disposeTileMesh(mesh: Mesh): void {
    mesh.geometry.dispose();

    const mat = mesh.material;
    if (Array.isArray(mat)) {
      mat.forEach((m) => m.dispose());
    } else {
      mat.dispose();
    }
  }

  // -------------------------
  // Your core “projection” hook
  // -------------------------

  private tileBoundsToWorldQuad(coord: TileCoord): WorldQuad {
    /**
     * This is intentionally a placeholder.
     *
     * You’ll implement:
     * - XYZ tile bounds in whatever coordinate system you use (WebMercator meters, local meters, etc.)
     * - Conversion to world space (x/z on plane; y up)
     *
     * The only contract: return a consistent quad with UVs mapping 0..1 across it.
     */
    return this.opts.tileBoundsToWorldQuad(coord);
  }
}
